import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  GetIndividualPortingRequestHistoryParams,
  GetIndividualPortingRequestHistoryResponse,
  GetIndividualPortingRequestsParams,
  GetIndividualPortingRequestsResponse,
} from 'src/app/core';
import { PortingOrigin } from 'src/app/core/proto/simon/sims-v2/enums/porting-origin';
import { ApiService } from 'src/app/core/services/api.service';

import {
  GetSimParams,
  GetSimsParams,
  GetSimsResponse,
  GetSimsSummaryParams,
  GetSimsSummaryResponse,
  GetSimsSummaryCSVParams,
  GetSimsSummaryCSVResponse,
  UpdateSimsParams,
  UpdateSimsResponse,
  CancelPendingPortingRequestParams,
  CancelPendingPortingRequestResponse,
  RequestIndvPortInParams,
  RequestIndvPortInResponse,
  GetCorporatePortingRequestParams,
  GetCorporatePortingRequestResponse,
  RequestCorporatePortInParams,
  RequestCorporatePortInResponse,
  GetCorporatePortingRequestDetailsParams,
  GetCorporatePortingRequestDetailsResponse,
  ScheduleCorporatePortingRequestParams,
  ScheduleCorporatePortingRequestResponse,
  GetPhoneNumberSelectionParams,
  GetPhoneNumberSelectionResponse,
} from '../dtos';
import { GetPhoneNumberAreaCodesParams } from '../dtos/get-phone-number-area-codes.params';
import { GetPhoneNumberAreaCodesResponse } from '../dtos/get-phone-number-area-codes.response';
import { SimsV2Sim } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class SimsV2Service {
  constructor(private readonly apiService: ApiService) {}

  /**
   * {mode: "+r", res: "sims.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  getSim(getSimParams: GetSimParams): Observable<SimsV2Sim> {
    const params: HttpParams = this.apiService.queryParams(getSimParams);

    return this.apiService.get(`/v2/sims/${getSimParams.iccid}`, params);
  }

  /**
   * {mode: "+r", res: "sims.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  getSims(getSimsParams: GetSimsParams): Observable<GetSimsResponse> {
    const params: HttpParams = this.apiService.queryParams(getSimsParams);
    return this.apiService.get(`/v2/sims`, params);
  }

  /**
   * {mode: "+u", res: "sims.*", dom: "[brandId,$distributorId]", tokenDom: "[brandUuid,distributorUuid]"}
   */
  updateSims(
    updateSimsParams: UpdateSimsParams
  ): Observable<UpdateSimsResponse> {
    return this.apiService.put(`/v2/sims`, updateSimsParams);
  }

  /**
   * {mode: "+r", res: "sims.*", dom: "[brandId,$distributorId]", tokenDom: "[brandUuid,distributorUuid]"}
   */
  getSimsSummary(
    getSimsSummaryParams: GetSimsSummaryParams
  ): Observable<GetSimsSummaryResponse> {
    return this.apiService.post(
      `/v2-internal/sims-summary`,
      getSimsSummaryParams
    );
  }

  getSimsSummaryCSV(
    getSimsSummaryCSVParams: GetSimsSummaryCSVParams,
    file
  ): Observable<GetSimsSummaryCSVResponse> {
    let formData = new FormData();
    formData.append('file', file);
    Object.keys(getSimsSummaryCSVParams).forEach((key) => {
      if (getSimsSummaryCSVParams[key]) {
        formData.append(key, getSimsSummaryCSVParams[key]);
      }
    });
    return this.apiService.postMultipart(
      `/v2-internal/sims-summary-csv`,
      formData
    );
  }

  /**
   * {mode: "+r", res: "phoneNumbers.*"}
   */
  getPhoneNumberAreaCodes(
    getPhoneNumberAreaCodesParams: GetPhoneNumberAreaCodesParams
  ): Observable<GetPhoneNumberAreaCodesResponse> {
    const params: HttpParams = this.apiService.queryParams(
      getPhoneNumberAreaCodesParams
    );
    return this.apiService.get(`/v2/phone-numbers/area-codes`, params);
  }

  /**
   * {mode: "+r", res: "phoneNumbers.*"}
   */
  getPhoneNumberSelection(
    getPhoneNumberSelectionParams: GetPhoneNumberSelectionParams
  ): Observable<GetPhoneNumberSelectionResponse> {
    const params: HttpParams = this.apiService.queryParams(
      getPhoneNumberSelectionParams
    );
    return this.apiService.get(`/v2/phone-numbers/selection`, params);
  }

  /**
   * {mode: "+u", res: "porting.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  getIndividualPortingRequests(
    getIndividualPortingRequestsParams: GetIndividualPortingRequestsParams
  ): Observable<GetIndividualPortingRequestsResponse> {
    const params: HttpParams = this.apiService.queryParams(
      getIndividualPortingRequestsParams
    );
    return this.apiService.get(
      `/v2-internal/phone-numbers/porting/individual`,
      params
    );
  }

  /**
   * {mode: "+u", res: "porting.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  getCorporatePortingRequest(
    getCorporatePortingRequestParams: GetCorporatePortingRequestParams
  ): Observable<GetCorporatePortingRequestResponse> {
    const params: HttpParams = this.apiService.queryParams(
      getCorporatePortingRequestParams
    );
    return this.apiService.get(
      `/v2-internal/phone-numbers/porting/corporate`,
      params
    );
  }
  /**
   * {mode: "+u", res: "phoneNumbers.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  requestIndvPortIn(
    requestIndvPortInParams: RequestIndvPortInParams
  ): Observable<RequestIndvPortInResponse> {
    requestIndvPortInParams.origin = PortingOrigin.ORIGIN_PORTAL;

    return this.apiService.post(
      `/v2/phone-numbers/port-in`,
      requestIndvPortInParams
    );
  }

  /**
   * {mode: "+u", res: "porting.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  requestCorporatePortIn(
    requestCorporatePortInParams: RequestCorporatePortInParams
  ): Observable<RequestCorporatePortInResponse> {
    let formData = new FormData();
    if (requestCorporatePortInParams.phoneNumbers) {
      formData.append(
        'phoneNumbers',
        JSON.stringify(requestCorporatePortInParams.phoneNumbers)
      );
    }

    Object.keys(requestCorporatePortInParams).forEach((key) => {
      if (requestCorporatePortInParams[key] && key !== 'phoneNumbers') {
        formData.append(key, requestCorporatePortInParams[key]);
      }
    });

    return this.apiService.postMultipart(
      `/v2-internal/phone-numbers/port-in/corporate`,
      formData
    );
  }

  /**
   * {mode: "+u", res: "porting.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  getIndividualPortingRequestHistory(
    getIndividualPortingRequestHistoryParams: GetIndividualPortingRequestHistoryParams
  ): Observable<GetIndividualPortingRequestHistoryResponse> {
    return this.apiService.get(
      `/v2/phone-numbers/${getIndividualPortingRequestHistoryParams.portingRequestId}/port-in`
    );
  }

  /**
   * {mode: "+d", res: "porting.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  cancelPendingPortingRequest(
    cancelPendingPortingRequestParams: CancelPendingPortingRequestParams
  ): Observable<CancelPendingPortingRequestResponse> {
    const params: HttpParams = this.apiService.queryParams(
      cancelPendingPortingRequestParams
    );
    return this.apiService.delete(
      `/v2/phone-numbers/${cancelPendingPortingRequestParams.portingRequestId}/port-in`,
      params
    );
  }

  /**
   * {mode: "+r", res: "porting.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  getCorporatePortingRequestDetails(
    getCorporatePortingRequestDetailsParams: GetCorporatePortingRequestDetailsParams
  ): Observable<GetCorporatePortingRequestDetailsResponse> {
    return this.apiService.get(
      `/v2-internal/phone-numbers/porting/corporate/${getCorporatePortingRequestDetailsParams.portingRequestId}`
    );
  }

  /**
   * {mode: "+u", res: "porting.*", dom: "[brandId]", tokenDom: "[brandUuid]"}
   */
  scheduleCorporatePortingRequest(
    scheduleCorporatePortingRequestParams: ScheduleCorporatePortingRequestParams
  ): Observable<ScheduleCorporatePortingRequestResponse> {
    return this.apiService.put(
      `/v2-internal/phone-numbers/${scheduleCorporatePortingRequestParams.portingRequestId}/schedule`,
      scheduleCorporatePortingRequestParams
    );
  }
}
